import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Aviso, Botao, CampoDeSelecao, Modal } from '../../componentes/ui';
import {
  CHAVE_DOS_QUADROS,
  carregarCardDaEmpresa,
  carregarQuadros,
  copiarCard,
  type CardDetalhado,
  type CardResumo,
} from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';

/**
 * "Copiar para um quadro": a mesma empresa em outro quadro.
 *
 * O card novo entra na primeira etapa do quadro escolhido, com as tags e quem
 * acompanha. Atividades, comentários e a data de execução são do processo do
 * card de origem e ficam nele. O quadro em que a empresa já está aparece na
 * lista, desabilitado — a empresa entra uma vez em cada quadro.
 */
export function CopiarParaQuadro(props: {
  readonly aberto: boolean;
  readonly card: CardDetalhado;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const navegar = useNavigate();
  const [quadroId, setQuadroId] = useState('');

  const quadros = useQuery({
    queryKey: CHAVE_DOS_QUADROS,
    queryFn: carregarQuadros,
    enabled: props.aberto,
  });
  // A mesma consulta do botão do CRM no cadastro da empresa.
  const daEmpresa = useQuery({
    queryKey: ['crm', 'empresa', props.card.businessId],
    queryFn: () => carregarCardDaEmpresa(props.card.businessId),
    enabled: props.aberto,
  });

  const copiar = useMutation({
    mutationFn: () => copiarCard(props.card.id, quadroId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['crm'] }),
  });

  // Cada abertura começa do zero.
  const { aberto } = props;
  const { reset } = copiar;
  useEffect(() => {
    if (!aberto) return;
    setQuadroId('');
    reset();
  }, [aberto, reset]);

  const jaEsta = new Set((daEmpresa.data?.cards ?? []).map((c) => c.quadroId));
  const outros = (quadros.data ?? []).filter((q) => q.id !== props.card.quadroId);
  const livres = outros.filter((q) => !jaEsta.has(q.id));
  const nomeDoQuadro = (id: string): string =>
    quadros.data?.find((q) => q.id === id)?.nome ?? 'escolhido';

  const abrirCopia = (copia: CardResumo): void => {
    props.aoFechar();
    navegar(`/crm/pipeline?quadro=${copia.quadroId}&card=${copia.id}`);
  };

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo="Copiar para um quadro"
      descricao={`${props.card.razaoSocial} entra no quadro escolhido, na primeira etapa.`}
      rodape={
        copiar.isSuccess ? (
          <>
            <Botao variante="secundario" onClick={props.aoFechar}>
              Fechar
            </Botao>
            <Botao onClick={() => abrirCopia(copiar.data)}>
              Abrir no quadro {nomeDoQuadro(copiar.data.quadroId)}
            </Botao>
          </>
        ) : (
          <>
            <Botao variante="secundario" onClick={props.aoFechar}>
              Cancelar
            </Botao>
            <Botao
              disabled={quadroId === ''}
              carregando={copiar.isPending}
              onClick={() => copiar.mutate()}
            >
              Copiar
            </Botao>
          </>
        )
      }
    >
      {copiar.isSuccess ? (
        <Aviso tom="positivo" titulo="Card copiado">
          {props.card.razaoSocial} está agora também no quadro {nomeDoQuadro(copiar.data.quadroId)},
          com as tags e quem acompanha.
        </Aviso>
      ) : (
        <div className="flex flex-col gap-4">
          {quadros.isSuccess && daEmpresa.isSuccess && livres.length === 0 ? (
            <Aviso tom="informacao" titulo="Não há para onde copiar">
              {outros.length === 0
                ? 'Este é o único quadro. Crie outro em “+ Quadro”, no seletor de quadros do Pipeline.'
                : 'A empresa já está em todos os outros quadros. Crie um novo em “+ Quadro”, no seletor de quadros do Pipeline.'}
            </Aviso>
          ) : (
            <CampoDeSelecao
              rotulo="Quadro"
              value={quadroId}
              onChange={(e) => setQuadroId(e.target.value)}
              opcoes={[
                {
                  valor: '',
                  rotulo: quadros.isPending || daEmpresa.isPending ? 'Carregando…' : 'Selecione…',
                },
                ...outros.map((q) => ({
                  valor: q.id,
                  rotulo: jaEsta.has(q.id) ? `${q.nome} — a empresa já está nele` : q.nome,
                  desabilitada: jaEsta.has(q.id),
                })),
              ]}
            />
          )}
          <p className="text-xs text-conteudo-sutil">
            Vão junto as tags e quem acompanha. Atividades, comentários e a data de execução ficam
            neste card.
          </p>
          {(quadros.error ?? daEmpresa.error) !== null && (
            <ErroDaApi
              titulo="Não foi possível carregar os quadros"
              erro={quadros.error ?? daEmpresa.error}
            />
          )}
          {copiar.isError && <ErroDaApi titulo="Não foi possível copiar" erro={copiar.error} />}
        </div>
      )}
    </Modal>
  );
}
